import type { FrontendPluginInfoResolver } from '@backstage/frontend-app-api';

type DefaultResolverSources = Parameters<
  Parameters<FrontendPluginInfoResolver>[0]['defaultResolver']
>[0];
type JsonObject = NonNullable<DefaultResolverSources['manifest']>;

// Webpack YAML/JSON `import()` returns `{ default: value }`. The default
// Backstage resolver only reads package.json description, not catalog
// `metadata.description`.
export const unwrapImportedValue = (value: unknown): unknown => {
  let current = value;
  while (
    current &&
    typeof current === 'object' &&
    !Array.isArray(current) &&
    'default' in current &&
    (current as { default: unknown }).default !== current
  ) {
    current = (current as { default: unknown }).default;
  }
  if (Array.isArray(current) && current.length === 1) {
    return current[0];
  }
  return current;
};

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const catalogDescription = (manifest: unknown): string | undefined => {
  if (!isJsonObject(manifest) || !isJsonObject(manifest.metadata)) {
    return undefined;
  }
  const description = manifest.metadata.description;
  return typeof description === 'string' && description
    ? description
    : undefined;
};

export const pluginInfoResolver: FrontendPluginInfoResolver = async ctx => {
  const packageJson = unwrapImportedValue(await ctx.packageJson());
  const manifest = unwrapImportedValue(await ctx.manifest());
  const { info } = await ctx.defaultResolver({
    packageJson: isJsonObject(packageJson) ? packageJson : undefined,
    manifest: isJsonObject(manifest) ? manifest : undefined,
  });
  const description = catalogDescription(manifest);

  return {
    info: {
      ...info,
      ...(description ? { description } : {}),
    },
  };
};
